import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { withErrorHandler } from './error-handler.util';
import { environment } from '../../environments';
import { Schedule } from '../interfaces/schedule';

@Injectable({
  providedIn: 'root'
})
export class TournamentService {
  baseURL = environment.API_URL + "tournament/"
  baseURLv2 = environment.API_URL + "v2/tournament/"
  baseUpLoadURL = environment.API_URL + "import-excel/"
  baseUploadURLv2 = environment.API_URL + "v2/tour_bus"
  baseEmbedURL = environment.API_URL + "api/tournaments/"

  constructor(private http: HttpClient) { }

  createTournament(body): Observable<any> {
    return this.http.post<any>(this.baseURL + "create-tournament", body).pipe(
      withErrorHandler({ operation: 'createTournament', timeoutMs: 30000, retries: 2 })
    );
  }

  getTestTabletoProduction(): Observable<any[]> {
    return this.http.get<any[]>(this.baseURL + "copy-to-test").pipe(
      withErrorHandler({ operation: 'getTestTabletoProduction', cache: true, fallback: [] })
    );
  }

  getTournamentFilter(body, pag: number, include_owner: boolean = false, filters?: any[]): Observable<any> {
    return this.http.post<any>(this.baseURL + "get-tournament-filter/" + pag, {
      ...body,
      timezone: new Date().getTimezoneOffset(),
      filters: filters
    },
      {
        params: {
          include_owner: include_owner.toString()
        }
      }).pipe(
        withErrorHandler({ operation: 'getTournamentFilter', cache: true, fallback: null })
      );
  }

  updateTournament(id, body): Observable<any[]> {
    return this.http.put<any[]>(this.baseURL + `update-tournament/${id}/main`, body).pipe(
      withErrorHandler({ operation: 'updateTournament', timeoutMs: 30000, retries: 1 })
    );
  }

  deleteAddTournament(id): Observable<any> {
    return this.http.delete<any>(this.baseURL + `delete-tournament/${id}/main`).pipe(
      withErrorHandler({ operation: 'deleteAddTournament', timeoutMs: 30000, retries: 1 })
    );
  }

  updateTournaments(id, body, custom): Observable<any> {
    return this.http.put<any>(this.baseURL + "update-tournament/" + id, body, {
      params: {
        custom: custom
      }
    }).pipe(
      withErrorHandler({ operation: 'updateTournaments', timeoutMs: 30000, retries: 1 })
    );
  }

  toggleTournaments(body): Observable<any> {
    return this.http.put<any>(this.baseURL + "manage-tournaments", body).pipe(
      withErrorHandler({ operation: 'toggleTournaments', timeoutMs: 30000, retries: 1 })
    );
  }

  updateUserLogin(id: number): Observable<any> {
    return this.http.post<any>(this.baseURL + "update-user-login", { id: id }).pipe(
      withErrorHandler({ operation: 'updateUserLogin', timeoutMs: 30000, retries: 1 })
    );
  }

  postFile(fileToUpload: File, checkUpload: number): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('uploadfile', fileToUpload, fileToUpload.name)
    console.info("formData:", formData)
    return this.http.post<any>(this.baseUpLoadURL + `/${checkUpload}/main`, formData);
  }

  postFile2(fileToUpload: File) {
    const formData: FormData = new FormData();
    formData.append('uploadfile', fileToUpload, fileToUpload.name)
    console.info("formData:", formData)
    return this.http.post<{
      msg: string,
      file: string,
      found: number,
      new: number,
    }>(this.baseUploadURLv2 + `/excel_upload`, formData).toPromise();
  }

  listOnTimeTournaments(): Observable<Schedule[]> {
    return this.http.get<Schedule[]>(this.baseURLv2 + "list-on-time").pipe(
      withErrorHandler({ operation: 'listOnTimeTournaments', cache: true, fallback: null })
    );
  }

  addBlinds(body: any): Observable<any> {
    return this.http.post<any>(this.baseURLv2 + "blind", body).pipe(
      withErrorHandler({ operation: 'addBlinds', timeoutMs: 30000, retries: 1 })
    );
  }

  listBlinds(
    name: string,
    site: number,
    speed: number,
    page: number,
    no_paging: boolean = false
  ): Observable<any> {
    const params: { [key: string]: string } = {
      name: name != "" ? name : undefined,
      site: !isNaN(site) ? site > 0 ? site.toString() : undefined : undefined,
      speed: !isNaN(speed) ? speed > 0 ? speed.toString() : undefined : undefined,
      page: !isNaN(page) ? page.toString() : undefined,
      no_paging: no_paging.toString()
    };

    Object.keys(params).forEach(
      (key) => params[key] === undefined && delete params[key]
    );

    return this.http.get(this.baseURLv2 + "blind", {
      params,
    }).pipe(
      withErrorHandler({ operation: 'listBlinds', cache: true, fallback: [] })
    );
  }

  getBlindByIds(Ids: number[]): Observable<any> {
    return this.http.post(this.baseURLv2 + "blind/list", { tournaments: Ids }).pipe(
      withErrorHandler({ operation: 'getBlindByIds', cache: true, fallback: [] })
    );
  }

  updateBlinds(id: number, body: any): Observable<any> {
    return this.http.put<any>(this.baseURLv2 + "blind/" + id, body).pipe(
      withErrorHandler({ operation: 'updateBlinds', timeoutMs: 30000, retries: 1 })
    );
  }

  deleteBlinds(id: number): Observable<any> {
    return this.http.delete<any>(this.baseURLv2 + "blind/" + id).pipe(
      withErrorHandler({ operation: 'deleteBlinds', timeoutMs: 30000, retries: 1 })
    );
  }

  increaseDecreaseHour(body: any): Observable<any> {
    return this.http.put<any>(this.baseURL + "edit-tools/offset-time", body).pipe(
      withErrorHandler({ operation: 'increaseDecreaseHour', timeoutMs: 30000, retries: 1 })
    );
  }

  getEmbeddedTournaments(id: string): Observable<any> {
    return this.http.get<any>(this.baseEmbedURL + "embedded/" + id).pipe(
      withErrorHandler({ operation: 'getEmbeddedTournaments', cache: true, fallback: {} })
    );
  }

  // Public, unauthenticated endpoint used by the /landing screen
  getTournamentPreview(filters?: {
    site?: string;
    from_time?: number;
    to_time?: number;
  }): Observable<any> {
    const params: { [key: string]: string } = {};

    if (filters?.site) {
      params.site = filters.site;
    }
    if (filters?.from_time != null) {
      params.from_time = String(filters.from_time);
    }
    if (filters?.to_time != null) {
      params.to_time = String(filters.to_time);
    }

    const cacheKey = Object.keys(params).length
      ? `getTournamentPreview:${JSON.stringify(params)}`
      : undefined;

    return this.http.get<any>(this.baseURLv2 + "tour-preview", { params }).pipe(
      withErrorHandler({ operation: 'getTournamentPreview', cache: true, cacheKey, fallback: null })
    );
  }

  getTournamentReportUrl() {
    return this.baseUploadURLv2 + "/tournament_report";
  }

}
